/**
 * Inline SVG icons for nav + chrome. Stroke-based, currentColor.
 * Pulled to match the Replit reference set (Home/Projects/Globe/Link/Shield/Gift/Cog).
 */

type IconProps = { size?: number; className?: string };
const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

export const IconSearch = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

export const IconPlus = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconImport = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <path d="M12 8v6m0 0-3-3m3 3 3-3" />
  </svg>
);

export const IconHome = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="m3 11 9-8 9 8" />
    <path d="M5 10v10h14V10" />
  </svg>
);

export const IconFolder = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
  </svg>
);

export const IconGlobe = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
  </svg>
);

export const IconPlug = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M9 2v6M15 2v6M6 10h12v3a6 6 0 0 1-12 0Z" />
    <path d="M12 19v3" />
  </svg>
);

export const IconShield = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6Z" />
  </svg>
);

export const IconGift = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7c-1.5-3-6-3-6 0 0 2 2 3 6 3 4 0 6-1 6-3 0-3-4.5-3-6 0Z" />
  </svg>
);

export const IconCog = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8L4.2 7a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
  </svg>
);

export const IconBulb = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12c1 1 2 2 2 4h4c0-2 1-3 2-4a7 7 0 0 0-4-12Z" />
  </svg>
);

export const IconBook = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2Z" />
    <path d="M4 19a2 2 0 0 1 2-2h12" />
  </svg>
);

export const IconChevronDown = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const IconArrowRight = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

export const IconArrowLeft = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M19 12H5M11 5l-7 7 7 7" />
  </svg>
);

/** Notifications bell. */
export const IconBell = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

/** VS Code / Replit-style "toggle left sidebar" panel icon. */
export const IconSidebarToggle = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M9 4v16" />
  </svg>
);

export const IconMic = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="9" y="2" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v4" />
  </svg>
);

export const IconUp = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const IconX = ({ size = 12 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export const IconMobile = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <path d="M11 18h2" />
  </svg>
);

export const IconWebsite = ({ size = 18 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="14" rx="2" />
    <path d="M3 8h18M7 6h.01M10 6h.01" />
  </svg>
);

export const IconDesign = ({ size = 18 }: IconProps) => (
  <svg {...base(size)}>
    <circle cx="13" cy="6" r="3" />
    <circle cx="6" cy="13" r="3" />
    <circle cx="13" cy="18" r="3" />
    <circle cx="19" cy="11" r="3" />
  </svg>
);

export const IconChart = ({ size = 18 }: IconProps) => (
  <svg {...base(size)}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </svg>
);

export const IconSlides = ({ size = 18 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="4" width="18" height="13" rx="1.5" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

export const IconTemplate = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M9 21V9" />
  </svg>
);

export const IconTerminal = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M6 9l3 3-3 3M12 15h5" />
  </svg>
);

export const IconDatabase = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M3 5v6c0 1.7 4 3 9 3s9-1.3 9-3V5M3 11v6c0 1.7 4 3 9 3s9-1.3 9-3v-6" />
  </svg>
);

export const IconQr = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM18 18h3v3h-3z" />
  </svg>
);

export const IconCreditCard = ({ size = 16 }: IconProps) => (
  <svg {...base(size)}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20M6 15h2" />
  </svg>
);

export const IconGoogle = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M21.6 12.227c0-.708-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.232c1.891-1.741 2.981-4.305 2.981-7.351z"
    />
    <path
      fill="#34A853"
      d="M12 22c2.7 0 4.965-.895 6.62-2.422l-3.232-2.51c-.896.6-2.041.955-3.388.955-2.604 0-4.808-1.76-5.595-4.122H3.064v2.59C4.71 19.778 8.09 22 12 22z"
    />
    <path
      fill="#FBBC05"
      d="M6.405 13.901a6.003 6.003 0 0 1 0-3.802V7.51H3.064a10.003 10.003 0 0 0 0 8.98l3.34-2.59z"
    />
    <path
      fill="#EA4335"
      d="M12 5.977c1.468 0 2.786.505 3.823 1.495l2.867-2.867C16.96 3.05 14.695 2 12 2 8.09 2 4.71 4.222 3.064 7.51l3.34 2.59C7.193 7.736 9.396 5.977 12 5.977z"
    />
  </svg>
);

export const IconTarget = ({ size = 14 }: IconProps) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v3M12 20v3M1 12h3M20 12h3" />
  </svg>
);

export const IconGithub = ({ size = 16 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
  </svg>
);
